<template>
  <div class="field-reference-edit position-relative" :class="{ 'readonly-field': calculated }">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <!-- Estado de carga para preload options -->
    <div
      v-if="field.preload && isLoadingPreloadOptions"
      class="text-center p-3"
    >
      <div
        class="spinner-border spinner-border-sm text-primary me-2"
        role="status"
      >
        <span class="visually-hidden">Cargando...</span>
      </div>
      <span class="text-muted">Cargando opciones...</span>
    </div>
    <div v-else>
      <!-- Lupa, agregar y editar juntos y del mismo tamaño: sueltos (la lupa
           adentro del input) se leían como tres cosas distintas. Con inlineLabel
           van al lado del label; sin él, flotan arriba a la derecha como antes. -->
      <div class="reference-head" :class="inlineLabel ? '' : 'reference-head--floating'">
        <span
          v-if="inlineLabel"
          class="reference-head-label"
          :class="value ? 'text-primary' : 'text-secondary'"
          >{{ inlineLabel
          }}<span v-if="inlineLabelRequired" class="text-danger ms-1"
            >* required</span
          ></span
        >
        <button
          v-if="!field.readonly && !isLocked"
          type="button"
          class="btn btn-sm btn-link reference-action-btn reference-search-btn"
          @click="openSearchModal"
          title="Buscar en popup"
        >
          <i class="material-icons">search</i>
        </button>
        <button
          v-if="field && !popup && field.ref !== 'users' && field.ref !== 'Proform'"
          type="button"
          class="btn btn-outline-secondary border-0 btn-sm reference-action-btn add-reference p-0"
          @click="handleAddClick"
        >
          <i class="material-icons">add</i>
        </button>
        <button
          v-if="value && !field.isArray && !popup && field.ref !== 'users' && field.ref !== 'Proform'"
          type="button"
          class="btn btn-outline-secondary border-0 btn-sm reference-action-btn edit-reference p-0"
          @click="handleEditClick"
        >
          <i class="material-icons">edit</i>
        </button>
      </div>

      <!-- Contenedor del multiselect -->
      <div class="d-flex align-items-center gap-2 reference-input-container">
        <!-- Multiselect para campos de referencia -->
        <multiselect
          :value="currentValue"
          :options="effectiveOptions"
          :multiple="field.isArray"
          :close-on-select="!field.isArray"
          :clear-on-select="true"
          :preserve-search="true"
          :disabled="field.readonly || isLocked"
          :preselect-first="false"
          :allow-empty="true"
          :searchable="true"
          :internal-search="false"
          :show-labels="true"
          label="name"
          track-by="_id"
          :placeholder="getPlaceholder()"
          class="mb-2 border-primary border-radius flex-grow-1"
          @input="onFieldChange"
          @remove="onFieldChange(null)"
          @search-change="onSearchChange"
          :custom-label="getMultiselectLabel"
          :loading="
            !field.preload &&
            isSearching &&
            (!effectiveOptions || effectiveOptions.length === 0)
          "
        >
          <template #selection="{ values, option }">
            <span v-if="field.isArray">
              <div v-for="(opt, i) in filteredValues(values)" :key="i">
                <span class="multiselect__tag">
                  {{ getMultiselectLabel(opt) }}
                  <span
                    class="multiselect__tag-icon"
                    @click.stop="
                      onFieldChange(values.filter((o) => o && o._id !== opt._id))
                    "
                  ></span>
                </span>
              </div>
            </span>
            <span v-else>
              <template v-if="option">
                {{ getMultiselectLabel(option) }}
                <span
                  class="multiselect__tag-icon"
                  @click.stop="onFieldChange(null)"
                ></span>
              </template>
            </span>
          </template>
        </multiselect>
      </div>
    </div>

    <!-- Modal de búsqueda -->
    <ReferenceSearchModal
      :is-visible="showSearchModal"
      :field="field"
      :current-value="currentValue"
      :reference-options="effectiveOptions"
      @select="onModalSelect"
      @close="showSearchModal = false"
    />
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import { wencesApi } from "@/api/wencesApi";
import ReferenceSearchModal from "../../common/ReferenceSearchModal.vue";
import {
  fetchShippingExpenseForLabel,
  formatMarkPaidExpenseLabel,
  typeofpaymentOptionsFromLists,
} from "@/utils/markPaidExpenseLabel";

export default {
  name: "EditFieldReference",
  components: {
    Multiselect,
    ReferenceSearchModal,
  },
  inject: {
    formContext: { default: null },
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    referenceOptions: {
      type: Array,
      default: () => [],
    },
    popup: {
      type: Boolean,
      default: false,
    },
    /** Label a dibujar en la misma línea que los botones (lo oculta quien nos usa). */
    inlineLabel: {
      type: String,
      default: "",
    },
    /** El label propio lleva la marca de obligatorio al costado. */
    inlineLabelRequired: {
      type: Boolean,
      default: false,
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      currentValue: null,
      localOptions: [],
      localOptionsBackup: [],
      isLoadingPreloadOptions: false,
      searchTimeout: null,
      isSearching: false,
      // Hay un término de búsqueda activo: las opciones ya vienen filtradas (server o filtro local),
      // así que no se debe caer al listado completo ni dejar que multiselect vuelva a filtrar.
      searchActive: false,
      internalReferenceOptions: [],
      showSearchModal: false,
      loadedReferenceObjects: new Map(),
      /** Cliente de cada embarque elegido que llegó sin él: _id del embarque → nombre. */
      shippingClientNames: {},
      /** Gasto elegido que llegó solo con el nombre (ficha del pago): _id → gasto completo. */
      expenseLabelDetails: {},
    };
  },
  computed: {
    isPaymentForm() {
      return (
        String((this.formContext && this.formContext.selectedModel) || "").toLowerCase() ===
        "payment"
      );
    },
    /** Gasto aplicado de un pago: se lee igual que en «Mark as paid» de To Be Paid. */
    usesPaymentExpenseLabel() {
      return this.field.ref === "shipping_expenses" && this.isPaymentForm;
    },
    /** Embarque de un pago: se ve con su cliente (OPERACIÓN - CLIENTE). */
    usesPaymentShippingLabel() {
      return String(this.field.ref || "").toLowerCase() === "shipping" && this.isPaymentForm;
    },
    /** Embarque elegido que no trae el nombre del cliente: hay que pedirlo aparte. */
    selectedShippingIdMissingClient() {
      if (!this.usesPaymentShippingLabel) return "";
      const cv = this.currentValue;
      if (!cv || Array.isArray(cv)) return "";
      const id = typeof cv === "string" ? cv : cv._id;
      if (!id) return "";
      if (typeof cv === "object" && cv.client && typeof cv.client === "object" && cv.client.name) {
        return "";
      }
      return String(id);
    },
    /** Gasto elegido sin tipo ni importe (llega solo con el nombre): hay que pedirlo aparte. */
    selectedExpenseIdMissingDetails() {
      if (!this.usesPaymentExpenseLabel) return "";
      const cv = this.currentValue;
      if (!cv || Array.isArray(cv)) return "";
      const id = typeof cv === "string" ? cv : cv._id;
      if (!id) return "";
      if (typeof cv === "object" && cv.amount != null) return "";
      return String(id);
    },
    isLocked() {
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
    effectiveOptions() {
      if (this.field.preload) {
        if (
          !this.searchActive &&
          this.localOptions.length === 0 &&
          this.referenceOptions.length > 0
        ) {
          return this.referenceOptions;
        }
        return this.localOptions;
      }
      return this.internalReferenceOptions;
    },
  },
  watch: {
    referenceOptions: {
      handler(newOptions) {
        // Las opciones inyectadas restringen el selector: si llegan después de la carga
        // completa (p. ej. se piden al abrir un modal), reemplazan a las cargadas.
        if (
          this.field.preload &&
          !this.searchActive &&
          Array.isArray(newOptions) &&
          newOptions.length > 0
        ) {
          this.localOptions = [...newOptions];
          this.localOptionsBackup = [...newOptions];
        }
      },
      immediate: true,
    },
    value: {
      handler(newValue) {
        if (!newValue) {
          this.currentValue = null;
          return;
        }
        if (newValue.__ob__ && this.isObserverEmptyValue(newValue)) {
          return;
        }
        if (
          newValue &&
          typeof newValue === "string" &&
          this.field &&
          this.field.ref
        ) {
          this.recoverReferenceObject(newValue, null, { skipEmit: true });
        } else if (Array.isArray(newValue) && this.field && this.field.ref) {
          this.currentValue = newValue;
          let resolved = newValue;
          newValue.forEach((item, index) => {
            if (item && typeof item === "string") {
              const full = this.effectiveOptions.find(
                (opt) => opt && (opt._id === item || String(opt._id) === String(item))
              );
              if (full) {
                resolved = resolved.slice();
                resolved[index] = full;
                this.currentValue = resolved;
              } else {
                this.recoverReferenceObject(item, index, { skipEmit: true });
              }
            } else if (item && this.hasIdButNoDisplayName(item)) {
              const full = this.effectiveOptions.find(
                (opt) => opt && (opt._id === item._id || String(opt._id) === String(item._id))
              );
              if (full) {
                resolved = resolved.slice();
                resolved[index] = full;
                this.currentValue = resolved;
              } else {
                this.recoverReferenceObject(item._id, index, { skipEmit: true });
              }
            }
          });
        } else if (
          this.field &&
          this.field.ref &&
          typeof newValue === "object" &&
          !Array.isArray(newValue) &&
          this.hasIdButNoDisplayName(newValue)
        ) {
          const full = this.effectiveOptions.find(
            (opt) => opt && (opt._id === newValue._id || String(opt._id) === String(newValue._id))
          );
          const idToFetch = newValue._id != null ? String(newValue._id) : null;
          if (full && !this.hasIdButNoDisplayName(full)) {
            this.currentValue = { ...full };
          } else if (idToFetch) {
            this.recoverReferenceObject(idToFetch, null, { skipEmit: true });
          }
        } else {
          this.currentValue = newValue;
        }
        if (this.field && !this.field.preload && newValue) {
          this.initializeWithCurrentValue();
        }
      },
      immediate: true,
    },
    currentValue: {
      handler(cv) {
        if (!cv || !this.field || !this.field.ref) return;
        if (cv.__ob__) return;
        this.rehydrateCurrentValueIfNeeded(cv);
      },
      deep: true,
    },
    selectedShippingIdMissingClient: {
      handler(id) {
        if (id) this.loadShippingClientName(id);
      },
      immediate: true,
    },
    selectedExpenseIdMissingDetails: {
      async handler(id) {
        if (!id || Object.prototype.hasOwnProperty.call(this.expenseLabelDetails, id)) return;
        // Marcado antes de pedirlo: el watcher puede volver a dispararse mientras tanto.
        this.$set(this.expenseLabelDetails, id, null);
        const doc = await fetchShippingExpenseForLabel(wencesApi, id);
        if (doc) this.$set(this.expenseLabelDetails, id, doc);
      },
      immediate: true,
    },
  },
  created() {
    if (!this.value) {
      this.currentValue = null;
      return;
    }
    const isObserver = !!this.value.__ob__;
    const isEmptyObserver = isObserver && this.isObserverEmptyValue(this.value);
    if (isObserver && isEmptyObserver) {
      return;
    }
    const isObserverNeedingRehydrate =
      this.value.__ob__ &&
      this.field &&
      this.field.ref &&
      (typeof this.value === "string" ||
        this.hasIdButNoDisplayName(this.value) ||
        (Array.isArray(this.value) &&
          this.value.some(
            (item) =>
              item &&
              (typeof item === "string" || this.hasIdButNoDisplayName(item))
          )));
    if (isObserverNeedingRehydrate) {
      this.$nextTick(() =>
        this.rehydrateCurrentValueIfNeeded(this.value)
      );
    } else {
      this.currentValue = this.value;
    }
  },
  async mounted() {
    if (!this.field || !this.field.ref) {
      console.warn("Field or field.ref is undefined in EditFieldReference mounted");
      return;
    }
    if (this.field.preload) {
      await this.loadAllOptions();
    } else {
      this.initializeWithCurrentValue();
    }
  },
  methods: {
    /** Observer con _id/name vacíos => considerar valor vacío y usar currentValue = null */
    isObserverEmptyValue(val) {
      if (!val || !val.__ob__) return false;
      if (typeof val === "string") return val === "";
      if (Array.isArray(val)) return val.length === 0;
      const id = val._id;
      return id == null || id === "";
    },
    /** Objeto tiene _id pero no name/label/textPlainVersion útil (incl. name === _id) */
    hasIdButNoDisplayName(val) {
      if (!val || typeof val !== "object" || Array.isArray(val)) return false;
      const id = val._id;
      if (id == null || id === "") return false;
      const idStr = String(id);
      if (val.name !== undefined && val.name !== "" && val.name !== id && String(val.name) !== idStr) return false;
      if (val.label !== undefined && val.label !== "" && val.label !== id && String(val.label) !== idStr) return false;
      if (val.textPlainVersion !== undefined && val.textPlainVersion !== "" && val.textPlainVersion !== id && String(val.textPlainVersion) !== idStr) return false;
      return true;
    },
    /** Si currentValue fue modificado y queda solo _id / name===_id, rehidratar sin emitir */
    rehydrateCurrentValueIfNeeded(cv) {
      if (!cv || !this.field || !this.field.ref) return;
      if (typeof cv === "string") {
        const full = this.effectiveOptions.find(
          (opt) => opt && (opt._id === cv || String(opt._id) === String(cv))
        );
        if (full) {
          this.currentValue = full;
        } else {
          this.recoverReferenceObject(cv, null, { skipEmit: true });
        }
        return;
      }
      if (Array.isArray(cv)) {
        let resolved = cv;
        let changed = false;
        cv.forEach((item, index) => {
          if (item && typeof item === "string") {
            const full = this.effectiveOptions.find(
              (opt) => opt && (opt._id === item || String(opt._id) === String(item))
            );
            if (full) {
              if (!changed) {
                resolved = cv.slice();
                changed = true;
              }
              resolved[index] = full;
            } else {
              this.recoverReferenceObject(item, index, { skipEmit: true });
            }
          } else if (item && this.hasIdButNoDisplayName(item)) {
            const full = this.effectiveOptions.find(
              (opt) => opt && (opt._id === item._id || String(opt._id) === String(item._id))
            );
            if (full) {
              if (!changed) {
                resolved = cv.slice();
                changed = true;
              }
              resolved[index] = full;
            } else {
              this.recoverReferenceObject(item._id, index, { skipEmit: true });
            }
          }
        });
        if (changed) this.currentValue = resolved;
        return;
      }
      if (this.hasIdButNoDisplayName(cv)) {
        const full = this.effectiveOptions.find(
          (opt) => opt && (opt._id === cv._id || String(opt._id) === String(cv._id))
        );
        if (full) {
          this.currentValue = full;
        } else {
          this.currentValue = cv;
          this.recoverReferenceObject(cv._id, null, { skipEmit: true });
        }
      }
    },
    initializeWithCurrentValue() {
      const currentValue = this.value;
      if (!currentValue) {
        return;
      }
      const optionsFromValue = [];
      if (this.field.isArray && Array.isArray(currentValue)) {
        currentValue.forEach((item) => {
          if (item) {
            optionsFromValue.push(
              typeof item === "string" ? { _id: item, name: item } : item
            );
          }
        });
      } else if (currentValue) {
        optionsFromValue.push(
          typeof currentValue === "string"
            ? { _id: currentValue, name: currentValue }
            : currentValue
        );
      }
      this.internalReferenceOptions = optionsFromValue;
    },
    async loadAllOptions() {
      if (!this.field || !this.field.preload || !this.field.ref) {
        return;
      }
      this.isLoadingPreloadOptions = true;
      try {
        const isUserRef = this.field.ref.toLowerCase() === 'user' || this.field.ref.toLowerCase() === 'users';
        const useInjectedOptions =
          !isUserRef &&
          Array.isArray(this.referenceOptions) &&
          this.referenceOptions.length > 0;

        let validData;
        if (useInjectedOptions) {
          validData = this.referenceOptions.filter((item) => item && (item._id || item.name));
        } else {
        let url;
        if (isUserRef) {
          url = '/api/users/all';
        } else {
          const params = new URLSearchParams();
          params.append("perPage", "1000");
          params.append("fields", "_id,name,label,textPlainVersion");
          if (this.field.refFilter && this.field.refFilterValue) {
            params.append(this.field.refFilter, this.field.refFilterValue);
          }
          url = `/api/gotrading/dynamic/${this.field.ref}?${params.toString()}`;
        }
        const { data } = await wencesApi.get(url);
        if (isUserRef) {
          validData = (data.data || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        } else {
          validData = (data.items || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        }
        }
        // Si mientras se cargaba la lista completa llegaron opciones inyectadas, mandan ellas.
        if (
          !isUserRef &&
          !useInjectedOptions &&
          Array.isArray(this.referenceOptions) &&
          this.referenceOptions.length > 0
        ) {
          validData = this.referenceOptions.filter((item) => item && (item._id || item.name));
        }
        const currentValue = this.value;
        let finalOptions = [...validData];
        if (currentValue) {
          if (this.field.isArray && Array.isArray(currentValue)) {
            currentValue.forEach((item) => {
              const itemId = typeof item === "string" ? item : item._id || item;
              const exists = finalOptions.some((opt) => opt._id === itemId);
              if (!exists && itemId) {
                finalOptions.push(
                  typeof item === "string" ? { _id: item, name: item } : item
                );
              }
            });
          } else {
            const currentId =
              typeof currentValue === "string"
                ? currentValue
                : currentValue._id || currentValue;
            const exists = finalOptions.some((opt) => opt._id === currentId);
            if (!exists && currentId) {
              finalOptions.push(
                typeof currentValue === "string"
                  ? { _id: currentValue, name: currentValue }
                  : currentValue
              );
            }
          }
        }
        this.localOptions = finalOptions;
        this.localOptionsBackup = [...finalOptions];
      } catch (error) {
        console.error(
          `Error loading all reference options for ${this.field.key}:`,
          error
        );
        this.localOptions = this.referenceOptions;
      } finally {
        this.isLoadingPreloadOptions = false;
      }
    },
    getPlaceholder() {
      if (!this.field) {
        return "Seleccione...";
      }
      if (this.field.preload) {
        return this.field.isArray
          ? "Seleccione una o más opciones..."
          : "Seleccione una opción...";
      }
      return this.field.isArray
        ? this.$t("Type to search records...")
        : this.$t("Type to search a record...");
    },
    /**
     * OPERACIÓN - TIPO - INV N - IMPORTE - COMISIÓN, como en To Be Paid. Solo cambia lo
     * que se ve: el valor sigue siendo el _id. Sin el gasto completo, el nombre de siempre.
     */
    paymentExpenseLabel(option) {
      if (!this.usesPaymentExpenseLabel || !option) return "";
      let expense =
        typeof option === "string"
          ? this.effectiveOptions.find((opt) => opt && opt._id === option) || { _id: option }
          : option;
      if (expense && expense._id && expense.amount == null) {
        const details = this.expenseLabelDetails[String(expense._id)];
        if (details) expense = { ...expense, ...details };
      }
      if (!expense || typeof expense !== "object" || !expense._id || expense.amount == null) {
        return "";
      }
      // En la tabla de Applied to cada celda es su propio formulario: el estado es la fila.
      const row =
        this.formContext && typeof this.formContext.getState === "function"
          ? this.formContext.getState()
          : null;
      const rowExpense = row && row.expense;
      const rowExpenseId =
        rowExpense && typeof rowExpense === "object" ? rowExpense._id : rowExpense;
      return formatMarkPaidExpenseLabel(expense, {
        typeOptions: typeofpaymentOptionsFromLists(this.formContext && this.formContext.lists),
        bankCommission:
          rowExpenseId && String(rowExpenseId) === String(expense._id)
            ? row.bank_commission
            : undefined,
      });
    },
    /** OPERACIÓN - CLIENTE; sin cliente, el nombre de siempre. */
    paymentShippingLabel(option) {
      if (!this.usesPaymentShippingLabel || !option) return "";
      const shipping =
        typeof option === "string"
          ? this.effectiveOptions.find((opt) => opt && opt._id === option)
          : option;
      if (!shipping || typeof shipping !== "object" || !shipping._id || !shipping.name) {
        return "";
      }
      // Placeholder {_id, name: _id} mientras se carga el embarque.
      if (String(shipping.name) === String(shipping._id)) return "";
      const client = shipping.client;
      const clientName =
        (client && typeof client === "object" && client.name) ||
        this.shippingClientNames[String(shipping._id)] ||
        "";
      return clientName ? `${shipping.name} - ${clientName}` : "";
    },
    async loadShippingClientName(shippingId) {
      const key = String(shippingId);
      if (Object.prototype.hasOwnProperty.call(this.shippingClientNames, key)) return;
      // Marcado antes de pedirlo: el watcher puede volver a dispararse mientras tanto.
      this.$set(this.shippingClientNames, key, "");
      try {
        const { data } = await wencesApi.get(`/api/gotrading/dynamic/shipping/${key}`);
        const doc = data && data.data && !Array.isArray(data.data) ? data.data : data;
        const client = doc && doc.client;
        if (client && typeof client === "object" && client.name) {
          this.$set(this.shippingClientNames, key, String(client.name));
        }
      } catch (error) {
        // Sin el embarque queda su nombre solo.
      }
    },
    getMultiselectLabel(option) {
      if (!option) return "No name";
      const expenseLabel = this.paymentExpenseLabel(option);
      if (expenseLabel) return expenseLabel;
      const shippingLabel = this.paymentShippingLabel(option);
      if (shippingLabel) return shippingLabel;
      if (typeof option === "string") {
        const optionFound = this.effectiveOptions.find(
          (opt) => opt._id === option
        );
        if (optionFound) {
          return (
            optionFound.name ||
            optionFound.label ||
            optionFound.textPlainVersion ||
            String(option)
          );
        }
        return String(option);
      }
      if (option._id && option.name && option._id == option.name) {
        const optionFound = this.effectiveOptions.find(
          (opt) => opt._id === option._id
        );
        return optionFound
          ? optionFound.name || optionFound.label || optionFound.textPlainVersion
          : String(option._id);
      }
      if (option.name) {
        return option.name;
      }
      if (option.label) {
        return option.label;
      }
      if (option.textPlainVersion) {
        return option.textPlainVersion;
      }
      // Si solo tenemos _id, buscar en effectiveOptions por si tiene name/label
      if (option._id) {
        const optionFound = this.effectiveOptions.find(
          (opt) => opt._id === option._id
        );
        if (optionFound) {
          return (
            optionFound.name ||
            optionFound.label ||
            optionFound.textPlainVersion ||
            String(option._id)
          );
        }
        if (typeof option._id === "object") {
          return option._id.name || option._id.label || "No name";
        }
        return String(option._id);
      }
      if (typeof option === "string" || typeof option === "number") {
        return String(option);
      }
      return "No name";
    },
    filteredValues(values) {
      if (!values) return [];
      return values.filter(
        (v) => v && (v._id || v.name || v.label || typeof v === "string")
      );
    },
    onFieldChange(value) {
      this.activelyEditing = true;
      this.currentValue = value;
      this.$emit("input", value);
    },
    openSearchModal() {
      this.showSearchModal = true;
    },
    onModalSelect(selectedValue) {
      let processedValue = selectedValue;
      if (this.field.isArray && Array.isArray(selectedValue)) {
        processedValue = selectedValue.map((item) => {
          if (typeof item === "string") {
            const found = this.effectiveOptions.find((opt) => opt._id === item);
            const result = found || { _id: item, name: item };
            if (!found && this.field.preload) {
              this.localOptions.push(result);
              this.localOptionsBackup.push(result);
            } else if (!found && !this.field.preload) {
              this.internalReferenceOptions.push(result);
            }
            return result;
          }
          if (item && !item.name && !item.label) {
            const found = this.effectiveOptions.find((opt) => opt._id === item._id);
            const result = found || item;
            if (!found && this.field.preload) {
              this.localOptions.push(result);
              this.localOptionsBackup.push(result);
            } else if (!found && !this.field.preload) {
              this.internalReferenceOptions.push(result);
            }
            return result;
          }
          const exists = this.effectiveOptions.some((opt) => opt._id === item._id);
          if (!exists) {
            if (this.field.preload) {
              this.localOptions.push(item);
              this.localOptionsBackup.push(item);
            } else {
              this.internalReferenceOptions.push(item);
            }
          }
          return item;
        });
      } else if (selectedValue) {
        if (typeof selectedValue === "string") {
          const found = this.effectiveOptions.find(
            (opt) => opt._id === selectedValue
          );
          processedValue = found || { _id: selectedValue, name: selectedValue };
          if (!found) {
            if (this.field.preload) {
              this.localOptions.push(processedValue);
              this.localOptionsBackup.push(processedValue);
            } else {
              this.internalReferenceOptions.push(processedValue);
            }
          }
        } else if (selectedValue._id) {
          if (!selectedValue.name && !selectedValue.label) {
            const found = this.effectiveOptions.find(
              (opt) => opt._id === selectedValue._id
            );
            processedValue = found || selectedValue;
          } else {
            processedValue = selectedValue;
          }
          const exists = this.effectiveOptions.some(
            (opt) => opt._id === processedValue._id
          );
          if (!exists) {
            if (this.field.preload) {
              this.localOptions.push(processedValue);
              this.localOptionsBackup.push(processedValue);
            } else {
              this.internalReferenceOptions.push(processedValue);
            }
          }
        }
      }
      this.currentValue = processedValue;
      this.onFieldChange(processedValue);
      if (!this.field.isArray) {
        this.showSearchModal = false;
      }
    },
    handleAddClick() {
      this.$emit("open-reference-modal", this.field, null);
    },
    handleEditClick() {
      let editingItem = this.value;
      if (
        editingItem &&
        editingItem._id &&
        !editingItem.name &&
        !editingItem.label
      ) {
        const fullItem = this.effectiveOptions.find(
          (opt) => opt._id === editingItem._id
        );
        if (fullItem) {
          editingItem = fullItem;
        }
      }
      this.$emit("open-reference-modal", this.field, editingItem);
    },
    async recoverReferenceObject(id, arrayIndex = null, options = {}) {
      const skipEmit = options.skipEmit === true;
      const idStr =
        id == null
          ? null
          : typeof id === "string"
            ? id
            : typeof id === "object" && id !== null && id._id != null
              ? (typeof id._id === "string" ? id._id : String(id._id))
              : String(id);
      if (!idStr || !this.field || !this.field.ref) {
        return;
      }
      if (this.loadedReferenceObjects.has(idStr)) {
        const cached = this.loadedReferenceObjects.get(idStr);
        if (arrayIndex !== null) {
          const newArray = [...this.value];
          newArray[arrayIndex] = cached;
          this.currentValue = newArray;
          if (!skipEmit) this.$emit("input", newArray);
        } else {
          this.currentValue = cached;
          if (!skipEmit) this.$emit("input", cached);
        }
        return;
      }
      try {
        const isUserRef = this.field.ref.toLowerCase() === 'user' || this.field.ref.toLowerCase() === 'users';
        let url;
        if (isUserRef) {
          url = `/api/users/${idStr}`;
        } else {
          url = `/api/gotrading/dynamic/${this.field.ref}/${idStr}`;
        }
        const { data } = await wencesApi.get(url);
        if (data) {
          this.loadedReferenceObjects.set(idStr, data);
          this.currentValue = data;
          if (arrayIndex !== null) {
            const newArray = [...this.value];
            newArray[arrayIndex] = data;
            if (!skipEmit) this.$emit("input", newArray);
          } else {
            if (!skipEmit) this.$emit("input", data);
          }
          // initializeWithCurrentValue deja {_id, name: _id} como opción: si ya
          // está, se reemplaza por el registro entero. Si no, el desplegable
          // mostraba el id en vez del nombre al abrirlo.
          const upsert = (list) => {
            const idx = list.findIndex((opt) => opt && String(opt._id) === String(data._id));
            if (idx === -1) list.push(data);
            else list.splice(idx, 1, data);
          };
          if (this.field.preload) {
            upsert(this.localOptions);
            upsert(this.localOptionsBackup);
          } else {
            upsert(this.internalReferenceOptions);
          }
        }
      } catch (error) {
        console.error(`❌ Error recuperando objeto para ID ${idStr}:`, error);
        const fallbackObject = { _id: idStr, name: idStr };
        if (arrayIndex !== null) {
          const newArray = [...this.value];
          newArray[arrayIndex] = fallbackObject;
          this.currentValue = newArray;
          if (!skipEmit) this.$emit("input", newArray);
        } else {
          this.currentValue = fallbackObject;
          if (!skipEmit) this.$emit("input", fallbackObject);
        }
      }
    },
    async onSearchChange(searchTerm) {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchActive = !!(searchTerm && searchTerm.trim().length >= 2);
      if (this.field.preload) {
        if (!searchTerm || searchTerm.trim().length < 2) {
          this.localOptions = [...this.localOptionsBackup];
        } else {
          const searchTermLower = searchTerm.toLowerCase().trim();
          // Pegar el ID del listado tiene que encontrar el registro (idModel es numérico).
          if (/^\d+$/.test(searchTermLower)) {
            const byId = this.localOptionsBackup.filter(
              (option) => String(option.idModel) === searchTermLower
            );
            if (byId.length > 0) {
              this.localOptions = byId;
              return;
            }
          }
          const filteredOptions = this.localOptionsBackup.filter((option) => {
            if (
              option.name &&
              option.name.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }
            if (
              option.label &&
              option.label.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }
            if (
              option.textPlainVersion &&
              option.textPlainVersion.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }
            for (const key in option) {
              if (
                key !== "_id" &&
                key !== "__v" &&
                key !== "createdAt" &&
                key !== "updatedAt" &&
                key !== "createdBy" &&
                key !== "updatedBy" &&
                key !== "deleted" &&
                key !== "editHistory"
              ) {
                const value = option[key];
                if (
                  typeof value === "string" &&
                  value.toLowerCase().includes(searchTermLower)
                ) {
                  return true;
                }
              }
            }
            return false;
          });
          this.localOptions = filteredOptions;
        }
        return;
      }
      if (!searchTerm || searchTerm.trim().length < 2) {
        this.initializeWithCurrentValue();
        return;
      }
      this.searchTimeout = setTimeout(async () => {
        try {
          await this.searchReferences(searchTerm);
        } catch (error) {
          console.error("Error in search:", error);
        }
      }, 300);
    },
    async searchReferences(searchTerm) {
      if (!this.field || !this.field.ref) {
        return;
      }
      this.isSearching = true;
      try {
        const isUserRef = this.field.ref.toLowerCase() === 'user' || this.field.ref.toLowerCase() === 'users';
        let url;
        if (isUserRef) {
          const params = new URLSearchParams();
          params.append("search", searchTerm.trim());
          params.append("perPage", "20");
          url = `/api/users/all?${params.toString()}`;
        } else {
          const params = new URLSearchParams();
          params.append("textPlainVersion", searchTerm.trim());
          params.append("perPage", "20");
          if (this.field.refFilter && this.field.refFilterValue) {
            params.append(this.field.refFilter, this.field.refFilterValue);
          }
          url = `/api/gotrading/dynamic/${this.field.ref}?${params.toString()}`;
        }
        const { data } = await wencesApi.get(url);
        let validData;
        if (isUserRef) {
          validData = (data.data || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        } else {
          validData = (data.items || data || []).filter(
            (item) => item && (item._id || item.name)
          );
        }
        const currentValue = this.value;
        let finalOptions = [...validData];
        if (currentValue) {
          if (this.field.isArray && Array.isArray(currentValue)) {
            currentValue.forEach((item) => {
              const itemId = typeof item === "string" ? item : item._id || item;
              const exists = finalOptions.some((opt) => opt._id === itemId);
              if (!exists && itemId) {
                finalOptions.push(
                  typeof item === "string" ? { _id: item, name: item } : item
                );
              }
            });
          } else {
            const currentId =
              typeof currentValue === "string"
                ? currentValue
                : currentValue._id || currentValue;
            const exists = finalOptions.some((opt) => opt._id === currentId);
            if (!exists && currentId) {
              finalOptions.push(
                typeof currentValue === "string"
                  ? { _id: currentValue, name: currentValue }
                  : currentValue
              );
            }
          }
        }
        this.internalReferenceOptions = finalOptions;
      } catch (error) {
        console.error(
          `Error searching references for ${this.field.key}:`,
          error
        );
        this.internalReferenceOptions = [];
      } finally {
        this.isSearching = false;
      }
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-reference-edit {
  width: 100%;
}

.reference-input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

/* Un nombre largo en una celda angosta: sin min-width:0 el flex item no encoge
   (min-width:auto = ancho del contenido) y el texto se monta sobre la columna
   de al lado en pantallas chicas. */
.reference-input-container :deep(.multiselect) {
  min-width: 0;
}

/* Label y botones en la misma línea. Gana a la regla global que posicionaba
   .add-reference / .edit-reference cada una por su lado. */
.field-reference-edit .reference-head {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
}

/* Mismo tamaño que los labels que dibuja el formulario (van en <small>). */
.field-reference-edit .reference-head-label {
  font-size: 0.875em;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-right: 2px;
}

/* Sin label propio no hay a qué pegarse: vuelve a flotar arriba a la derecha. */
.field-reference-edit .reference-head--floating {
  position: absolute;
  top: -22px;
  right: 0;
  z-index: 9;
}

.field-reference-edit .reference-head .reference-action-btn {
  position: static !important;
  top: auto !important;
  right: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 0 !important;
  color: #6c757d !important;
  text-decoration: none !important;
  border: none !important;
  background: transparent !important;
  cursor: pointer;
  flex-shrink: 0;
}

.field-reference-edit .reference-head .reference-action-btn:hover {
  color: var(--bs-primary) !important;
  background: transparent !important;
}

/* Los tres iconos del mismo tamaño: la lupa venía a 20px y add/edit a 24px. */
.field-reference-edit .reference-head .reference-action-btn .material-icons {
  font-size: 18px;
  line-height: 1;
}

/* En la fila compacta los botones se achican para que la línea del label mida
   lo mismo que en las demás celdas y los valores queden a la misma altura. */
.dyn-array-row .field-reference-edit .reference-head .reference-action-btn {
  width: 15px;
  height: 15px;
}

.dyn-array-row .field-reference-edit .reference-head .reference-action-btn .material-icons {
  font-size: 14px;
}

:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}

.border-primary {
  border-color: #007bff !important;
}

.border-radius {
  border-radius: 0.375rem !important;
}
</style>

